import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { RadioGroupComponent, RadioComponent, RadioModule } from '@vg-constellation/angular-17/radio';
import { DropdownModule, DropdownMenuData, DropdownMenuItem } from '@vg-constellation/angular-17/dropdown';
import { InputComponent } from '@vg-constellation/angular-17/input';
import { LabelComponent } from '@vg-constellation/angular-17/label';
import { ButtonComponent } from '@vg-constellation/angular-17/button';
import { LinkComponent } from '@vg-constellation/angular-17/link';
import { HintErrorComponent } from '@vg-constellation/angular-17/hint-error';
import { DatePickerComponent, DatePickerModule } from '@vg-constellation/angular-17/date-picker';
import { IconModule } from '@vg-constellation/angular-17/icon';
import { TooltipComponent, TooltipModule } from '@vg-constellation/angular-17/tooltip';
import { CardComponent } from '@vg-constellation/angular-17/card';

import { BeneficiaryType } from '../../types/BeneficiaryType';
import { BeneficiarySelection, BeneficiarySectionType, DropdownStateChangeEvent, getCardDensity, US_STATES_AND_TERRITORIES } from '@models/beneficiary.model';
import { Person } from '@services/zok';
import { WebLogger } from '@weps/shared-http';
import * as content from '@services/content/guided-flow/account-details-content';

interface RadioStateChangeEvent {
  inputId?: string;
}

@Component({
  selector: 'app-input-beneficiary-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RadioGroupComponent,
    RadioComponent,
    RadioModule,
    DropdownModule,
    InputComponent,
    LabelComponent,
    ButtonComponent,
    LinkComponent,
    HintErrorComponent,
    DatePickerComponent,
    DatePickerModule,
    IconModule,
    TooltipComponent,
    TooltipModule,
    CardComponent,
  ],
  templateUrl: './input-beneficiary-card.component.html',
  styleUrls: ['./input-beneficiary-card.component.scss'],
})
export class InputBeneficiaryCardComponent implements OnInit, OnChanges {
  private logEvent(eventType: string, payload: Record<string, unknown> = {}, level: 'info' | 'warn' | 'error' = 'info'): void {
    WebLogger.log({ message: `InputBeneficiaryCard: ${eventType}`, level, payload: { event_type: eventType, ...payload } });
  }

  @Input() mode: 'add' | 'edit' = 'add';
  @Input() existingData: BeneficiarySelection | null = null;
  @Input() householdMembers: DropdownMenuItem[] = [];
  @Input() householdPeople: Person[] = [];
  @Input() section: BeneficiarySectionType = 'PRIMARY';
  @Input() showCancelOnTypeSelection = false;
  @Input() skipTypeSelection = false;
  @Input() isSaving = false;

  @Output() save = new EventEmitter<BeneficiarySelection>();
  @Output() cancel = new EventEmitter<void>();

  protected readonly content = content;
  protected readonly BeneficiaryType = BeneficiaryType;

  // State tracking
  currentState: 'type-selection' | 'person-dropdown' | 'inline-form' | 'trust-placeholder' = 'type-selection';
  selectedBeneficiaryType: BeneficiaryType | null = null;

  // Form fields
  firstName = '';
  lastName = '';
  dateOfBirth = '';
  stateOfResidence = '';
  selectedPersonId: string | null = null;

  // Validation flags
  validFirstName = true;
  validLastName = true;
  validDateOfBirth = true;
  validStateOfResidence = true;

  // Dropdown data
  personDropdownData: DropdownMenuData = { groups: [] };

  // State dropdown options
  stateOptions: DropdownMenuData = { groups: [{ items: US_STATES_AND_TERRITORIES }] };

  // Card density for responsive sizing
  cardDensity: 'small' | 'medium' = 'medium';

  /** Temporarily hide type change until Trust support is implemented */
  showBeneficiaryTypeChange = false;

  ngOnInit(): void {
    this.buildPersonDropdown();
    this.cardDensity = getCardDensity();

    if (this.mode === 'edit' && this.existingData) {
      this.populateFromExistingData();
    } else if (this.skipTypeSelection && this.mode === 'add') {
      this.selectedBeneficiaryType = BeneficiaryType.INDIVIDUAL_BY_NAME;
      this.currentState = 'person-dropdown';
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.cardDensity = getCardDensity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Rebuild dropdown when householdMembers input changes (fixes race condition when ZOK loads after component init)
    if (changes['householdMembers'] && !changes['householdMembers'].firstChange) {
      this.buildPersonDropdown();
    }

    // Repopulate form when existingData changes (edit mode)
    if (changes['existingData'] && !changes['existingData'].firstChange && this.mode === 'edit') {
      this.populateFromExistingData();
    }
  }

  private populateFromExistingData(): void {
    if (!this.existingData) return;
    this.firstName = this.existingData.firstName;
    this.lastName = this.existingData.lastName;
    this.dateOfBirth = this.existingData.dateOfBirth;
    this.stateOfResidence = this.existingData.stateOfResidence;

    // If DOB or state are missing and we have a householdMemberId, look up the person data
    if (this.existingData.householdMemberId && (!this.dateOfBirth || !this.stateOfResidence)) {
      const person = this.householdPeople.find(p => p.householdMemberId === this.existingData!.householdMemberId);
      if (person) {
        if (!this.dateOfBirth && person.birthDate) {
          this.dateOfBirth = person.birthDate;
        }
        if (!this.stateOfResidence && person.state) {
          this.stateOfResidence = person.state;
        }
      }
    }

    this.selectedBeneficiaryType = this.existingData.beneficiaryType;
    this.currentState = 'inline-form';
  }

  private buildPersonDropdown(): void {
    const addNewItem: DropdownMenuItem = {
      labelText: content.BENEFICIARY_ADD_NEW_PERSON,
      id: 'add-new-person',
    };

    this.personDropdownData = {
      groups: [{
        items: [addNewItem, ...this.householdMembers],
      }],
    };
  }

  handleTypeSelection(event: RadioStateChangeEvent): void {
    const inputId = event?.inputId;
    if (inputId === 'bene-type-individual') {
      this.selectedBeneficiaryType = BeneficiaryType.INDIVIDUAL_BY_NAME;
      this.currentState = this.mode === 'edit' ? 'inline-form' : 'person-dropdown';
    } else if (inputId === 'bene-type-trust') {
      this.selectedBeneficiaryType = BeneficiaryType.TRUST;
      this.currentState = 'trust-placeholder';
    }
    this.logEvent('input_bene_card_type_selected', { type: this.selectedBeneficiaryType, next_state: this.currentState, mode: this.mode });
  }

  handleChangeType(): void {
    this.currentState = 'type-selection';
    this.selectedBeneficiaryType = null;
    // Only reset form in add mode; preserve data in edit mode
    if (this.mode === 'add') {
      this.resetForm();
    }
  }

  handlePersonDropdownChange(event: DropdownStateChangeEvent): void {
    const selectedId = event?.activeItems?.[0];
    if (selectedId === 'add-new-person') {
      this.logEvent('input_bene_card_add_new_person', { section: this.section });
      this.currentState = 'inline-form';
      this.selectedPersonId = null;
    } else if (selectedId) {
      this.logEvent('input_bene_card_existing_person_selected', { member_id: selectedId, section: this.section });
      this.selectedPersonId = selectedId;
      const selectedMember = this.householdMembers.find(m => m.id === selectedId);
      if (selectedMember) {
        this.emitExistingPersonSelection(selectedMember);
      }
    }
  }

  handleInlineFormPersonChange(event: DropdownStateChangeEvent): void {
    const selectedId = event?.activeItems?.[0];
    if (selectedId && selectedId !== 'add-new-person') {
      const selectedMember = this.householdMembers.find(m => m.id === selectedId);
      if (selectedMember) {
        this.emitExistingPersonSelection(selectedMember);
      }
    }
  }

  private emitExistingPersonSelection(member: DropdownMenuItem): void {
    const memberId = member.id?.toString() || null;
    const person = this.householdPeople.find(p => p.householdMemberId === memberId);

    this.logEvent('input_bene_card_emit_existing_person', {
      member_id: memberId, found_in_household: !!person,
      section: this.section,
    });
    const bene: BeneficiarySelection = {
      householdMemberId: memberId,
      firstName: person?.name?.firstName || '',
      lastName: person?.name?.lastName || '',
      dateOfBirth: person?.birthDate || '',
      stateOfResidence: person?.state || '',
      type: this.section,
      distributionPercentage: 0,
      isNew: false,
      beneficiaryType: BeneficiaryType.INDIVIDUAL_BY_NAME,
    };
    this.save.emit(bene);
  }

  handleSave(): void {
    if (!this.validateForm()) {
      this.logEvent('input_bene_card_save_validation_failed', {
        valid_first_name: this.validFirstName, valid_last_name: this.validLastName,
        valid_dob: this.validDateOfBirth, valid_state: this.validStateOfResidence,
      }, 'warn');
      return;
    }

    const bene: BeneficiarySelection = {
      householdMemberId: this.existingData?.householdMemberId || null,
      firstName: this.firstName.trim(),
      lastName: this.lastName.trim(),
      dateOfBirth: this.dateOfBirth,
      stateOfResidence: this.stateOfResidence,
      type: this.section,
      distributionPercentage: this.existingData?.distributionPercentage || 0,
      isNew: this.mode === 'add',
      beneficiaryType: this.selectedBeneficiaryType || BeneficiaryType.INDIVIDUAL_BY_NAME,
    };

    this.logEvent('input_bene_card_save_emitted', {
      member_id: bene.householdMemberId, is_new: bene.isNew, section: bene.type, beneficiary_type: bene.beneficiaryType,
    });
    this.save.emit(bene);
  }

  handleCancel(): void {
    this.cancel.emit();
  }

  handleStateDropdownChange(event: DropdownStateChangeEvent): void {
    this.stateOfResidence = event?.activeItems?.[0] || '';
    this.validStateOfResidence = !!this.stateOfResidence;
  }

  handleDateChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.dateOfBirth = target?.value || '';
    this.validDateOfBirth = !!this.dateOfBirth;
  }

  // Called from template (blur) events for individual field validation
  validateFirstName(): void { this.validFirstName = !!this.firstName.trim(); }
  validateLastName(): void { this.validLastName = !!this.lastName.trim(); }
  validateDateOfBirth(): void { this.validDateOfBirth = !!this.dateOfBirth; }

  private validateForm(): boolean {
    this.validateFirstName();
    this.validateLastName();
    this.validateDateOfBirth();
    this.validStateOfResidence = !!this.stateOfResidence;
    return this.validFirstName && this.validLastName && this.validDateOfBirth && this.validStateOfResidence;
  }

  private resetForm(): void {
    this.firstName = '';
    this.lastName = '';
    this.dateOfBirth = '';
    this.stateOfResidence = '';
    this.selectedPersonId = null;
    this.validFirstName = true;
    this.validLastName = true;
    this.validDateOfBirth = true;
    this.validStateOfResidence = true;
  }
}
